import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle2, ArrowRight, Award } from 'lucide-react';

const CarriersPage = () => {
  const carriers = [
    {
      name: "MassMutual",
      fullName: "Massachusetts Mutual Life Insurance Company",
      rating: "A++",
      founded: "1851",
      assets: "$276B+",
      description: "One of the oldest and most respected mutual life insurance companies in America."
    },
    {
      name: "New York Life",
      fullName: "New York Life Insurance Company",
      rating: "A++",
      founded: "1845",
      assets: "$350B+",
      description: "The largest mutual life insurance company in the United States."
    },
    {
      name: "Guardian Life",
      fullName: "Guardian Life Insurance Company of America",
      rating: "A++",
      founded: "1860",
      assets: "$90B+",
      description: "A premier mutual company known for whole life and disability insurance."
    },
    {
      name: "Northwestern Mutual",
      fullName: "Northwestern Mutual",
      rating: "A++",
      founded: "1857",
      assets: "$308B+",
      description: "Consistently ranked among the most financially secure life insurance companies."
    },
    {
      name: "Pacific Life",
      fullName: "Pacific Life Insurance Company",
      rating: "A+",
      founded: "1868",
      assets: "$200B+",
      description: "Leading provider of innovative life insurance and annuity products."
    },
    {
      name: "Lincoln Financial",
      fullName: "Lincoln Financial Group",
      rating: "A+",
      founded: "1905",
      assets: "$350B+",
      description: "Diversified financial services with strong life insurance offerings."
    },
    {
      name: "Penn Mutual",
      fullName: "Penn Mutual Life Insurance Company",
      rating: "A+",
      founded: "1847",
      assets: "$20B+",
      description: "One of America's oldest mutual life insurers with dividend-paying policies."
    },
    {
      name: "Protective Life",
      fullName: "Protective Life Corporation",
      rating: "A+",
      founded: "1907",
      assets: "$115B+",
      description: "Strong financial ratings and diverse life insurance product portfolio."
    },
    {
      name: "Allianz Life",
      fullName: "Allianz Life Insurance Company of North America",
      rating: "A+",
      founded: "1979",
      assets: "$60B+",
      description: "Part of global financial services leader Allianz SE."
    },
    {
      name: "Nationwide",
      fullName: "Nationwide Life Insurance Company",
      rating: "A+",
      founded: "1926",
      assets: "$250B+",
      description: "Fortune 100 company with comprehensive insurance solutions."
    },
    {
      name: "Athene",
      fullName: "Athene Annuity and Life Company",
      rating: "A",
      founded: "2009",
      assets: "$250B+",
      description: "Rapidly growing provider of retirement savings products."
    },
    {
      name: "F&G",
      fullName: "Fidelity & Guaranty Life",
      rating: "A-",
      founded: "1959",
      assets: "$40B+",
      description: "Specialist in fixed indexed annuities and life insurance."
    }
  ];

  const ratingExplanation = [
    {
      rating: "A++ / A+ (Superior)",
      description: "Exceptional financial security. Highest ability to meet ongoing insurance obligations."
    },
    {
      rating: "A / A- (Excellent)",
      description: "Very strong financial security with excellent ability to meet obligations."
    }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="carriers-page">
      {/* Hero Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 block">
              Our Partners
            </span>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Insurance Carriers & Financial Strength
            </h1>
            <p className="text-slate-300 text-xl mb-8 leading-relaxed">
              We partner exclusively with America's most financially stable insurance carriers—companies that have demonstrated the ability to fulfill promises for generations.
            </p>
          </div>
        </div>
      </section>

      {/* Rating Importance */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">
                Why Ratings Matter
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Life insurance is a long-term commitment—often spanning decades. The financial strength of your chosen carrier determines whether they'll be able to pay claims when your family needs them most.
              </p>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Independent rating agencies like A.M. Best, Moody's, Standard & Poor's, and Fitch assess insurers' ability to meet their ongoing financial obligations. We work only with carriers that maintain the highest ratings.
              </p>
            </div>

            <div className="space-y-4">
              {ratingExplanation.map((item, index) => (
                <div key={index} className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
                  <div className="flex items-center space-x-3 mb-2">
                    <Award className="w-5 h-5 text-[#C5A059]" />
                    <span className="text-white font-semibold">{item.rating}</span>
                  </div>
                  <p className="text-slate-400 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Carriers Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl text-white mb-4">
              Our Partner Carriers
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Compare quotes from these top-rated insurance carriers through our platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {carriers.map((carrier, index) => (
              <div
                key={index}
                className="bg-slate-900/50 border border-slate-800 p-6 hover:border-[#C5A059]/30 transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-serif text-white uppercase tracking-wider text-lg mb-1">
                      {carrier.name}
                    </h3>
                    <p className="text-slate-500 text-xs">{carrier.fullName}</p>
                  </div>
                  <span className="px-3 py-1 bg-[#C5A059]/10 text-[#C5A059] text-xs font-bold rounded">
                    {carrier.rating}
                  </span>
                </div>
                
                <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                  {carrier.description}
                </p>

                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Founded {carrier.founded}</span>
                  <span>Assets: {carrier.assets}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-start space-x-4 text-sm text-slate-500">
            <Shield className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
            <p>
              Financial strength ratings are as of the most recent evaluation by each rating agency and are subject to change. Ratings reflect the carrier's ability to meet policyholder obligations and are not recommendations to purchase any product. Always review the most current ratings before making insurance decisions.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">
            Compare Quotes from Top Carriers
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            See competitive quotes from these A+ rated carriers in minutes.
          </p>
          <Link
            to="/compare"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all group"
          >
            Compare Life Insurance Quotes
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CarriersPage;
